import { fetchAllClientsAndProjectsThunk } from "Actions";
import { saveProjectSettings } from "features/project";
import { useNotification, useSelectedProject } from "Hooks";
import produce from "immer";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Radio,
  Stack,
  Typography,
  alpha,
  styled,
} from "@mui/material";
import {
  readConsentFromProjectSettings,
  writeConsentToProjectSettings,
} from "features/consent/projectConsentSettings";
import type {
  ConsentCapture,
  ConsentEnforcement,
  NoConsentBehavior,
} from "features/consent/types";

export type {
  ConsentCapture,
  ConsentEnforcement,
  NoConsentBehavior,
} from "features/consent/types";

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(11),
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
}));

const OptionCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$selected" && prop !== "$disabled",
})<{ $selected?: boolean; $disabled?: boolean }>(
  ({ theme, $selected, $disabled }) => ({
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${
      $selected ? theme.palette.primary.main : theme.palette.divider
    }`,
    padding: theme.spacing(2),
    cursor: $disabled ? "default" : "pointer",
    opacity: $disabled ? 0.65 : 1,
    pointerEvents: $disabled ? "none" : "auto",
    backgroundColor: $selected
      ? alpha(theme.palette.primary.main, 0.08)
      : theme.palette.background.paper,
    transition: theme.transitions.create(["border-color", "background-color"], {
      duration: theme.transitions.duration.shorter,
    }),
    "&:hover": {
      borderColor: $disabled
        ? undefined
        : $selected
          ? theme.palette.primary.main
          : theme.palette.divider2,
    },
  }),
);

const RecommendedBadge = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "2px 8px",
  borderRadius: 999,
  fontSize: theme.typography.pxToRem(10),
  fontWeight: 600,
  lineHeight: 1.2,
  color: theme.palette.common.white,
  backgroundColor: theme.palette.success.main,
}));

const ActionsRow = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  justifyContent: "flex-end",
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(3),
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

type ConsentOptionRowProps = {
  selected: boolean;
  onSelect: () => void;
  title: string;
  description: string;
  recommended?: boolean;
  disabled?: boolean;
};

function ConsentOptionRow({
  selected,
  onSelect,
  title,
  description,
  recommended,
  disabled,
}: ConsentOptionRowProps) {
  return (
    <OptionCard
      $selected={selected}
      $disabled={disabled}
      onClick={disabled ? undefined : onSelect}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Radio
          checked={selected}
          disableRipple
          tabIndex={-1}
          sx={{ p: 0, mt: -0.25 }}
        />
        <Box flex={1} minWidth={0}>
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            mb={0.5}
          >
            <Typography variant="subtitle1" component="span">
              {title}
            </Typography>
            {recommended ? (
              <RecommendedBadge component="span">Recommended</RecommendedBadge>
            ) : null}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </Stack>
    </OptionCard>
  );
}

function ConsentModeSettings() {
  const dispatch = useDispatch();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const { projectSettings, setProjectSettings, selectedProject } =
    useSelectedProject();

  const [capture, setCapture] = useState<ConsentCapture>("auto");
  const [enforcement, setEnforcement] =
    useState<ConsentEnforcement>("do_not_require");
  const [noConsent, setNoConsent] = useState<NoConsentBehavior>("full");
  const [saving, setSaving] = useState(false);

  const {
    capture: serverCapture,
    enforcement: serverEnforcement,
    noConsent: serverNoConsent,
  } = readConsentFromProjectSettings(projectSettings);

  useEffect(() => {
    setCapture(serverCapture);
    setEnforcement(serverEnforcement);
    setNoConsent(serverNoConsent);
  }, [selectedProject?.id, serverCapture, serverEnforcement, serverNoConsent]);

  const dirty =
    capture !== serverCapture ||
    enforcement !== serverEnforcement ||
    noConsent !== serverNoConsent;
  const showAdvancedConsentSections = enforcement === "require";

  const formDisabled = saving || !selectedProject?.id;

  const handleCancel = useCallback(() => {
    setCapture(serverCapture);
    setEnforcement(serverEnforcement);
    setNoConsent(serverNoConsent);
  }, [serverCapture, serverEnforcement, serverNoConsent]);

  const handleRequireConsentSelect = useCallback(() => {
    const switchingFromDefault =
      enforcement === "do_not_require" &&
      serverEnforcement === "do_not_require";

    setEnforcement("require");

    if (switchingFromDefault && !dirty) {
      setCapture("auto");
      setNoConsent("anonymised");
    }
  }, [dirty, enforcement, serverEnforcement]);

  const handleSave = useCallback(async () => {
    if (!selectedProject?.id || !selectedProject.clientId) {
      return;
    }
    setSaving(true);
    try {
      const next = produce(projectSettings, (draft) => {
        writeConsentToProjectSettings(draft, {
          capture,
          enforcement,
          noConsent,
        });
      });
      await saveProjectSettings(next, selectedProject);
      setProjectSettings(next);
      dispatch(
        fetchAllClientsAndProjectsThunk({
          clientId: selectedProject.clientId as number,
          projectId: selectedProject.id as number,
        }),
      );
      showSuccessNotification("Consent settings saved");
    } catch {
      showErrorNotification(
        "Could not save consent settings. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  }, [
    capture,
    enforcement,
    noConsent,
    dispatch,
    projectSettings,
    selectedProject,
    setProjectSettings,
    showErrorNotification,
    showSuccessNotification,
  ]);

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" mb={3}>
        Configure when the tag is allowed to run, how consent is captured, and
        what to do when users have not consented.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        These are advanced consent settings for setups where your cookie banner
        or CMP does not block the Incendium script by default. If you are using
        a standard blocking banner flow, you usually do not need to change these
        options.
      </Typography>

      <Stack spacing={3}>
        <Box>
          <SectionLabel gutterBottom display="block">
            Consent enforcement
          </SectionLabel>
          <Stack spacing={1.5}>
            <ConsentOptionRow
              selected={enforcement === "require"}
              onSelect={handleRequireConsentSelect}
              title="Require consent before tracking"
              description="The tag does not run in a full tracking mode until the user has given the consent you configure."
              recommended
              disabled={formDisabled}
            />
            <ConsentOptionRow
              selected={enforcement === "do_not_require"}
              onSelect={() => setEnforcement("do_not_require")}
              title="Do not require consent"
              description="The tag may run without waiting for explicit consent where your lawful basis allows. You remain responsible for compliance."
              disabled={formDisabled}
            />
          </Stack>
        </Box>

        {showAdvancedConsentSections ? (
          <>
            <Box>
              <SectionLabel gutterBottom display="block">
                Consent capture
              </SectionLabel>
              <Stack spacing={1.5}>
                <ConsentOptionRow
                  selected={capture === "auto"}
                  onSelect={() => setCapture("auto")}
                  title="Auto-detect consent signals"
                  description="We detect common consent platforms and consent signals on the page and stay in sync automatically."
                  recommended
                  disabled={formDisabled}
                />
                <ConsentOptionRow
                  selected={capture === "manual"}
                  onSelect={() => setCapture("manual")}
                  title="Manual integration (advanced)"
                  description="After the Incendium tag loads, call inc('consent_update', …) from your CMP or app code whenever consent changes (see below)."
                  disabled={formDisabled}
                />
              </Stack>
              {capture === "auto" ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2" component="div" gutterBottom>
                    Your consent management platform (CMP) script must load{" "}
                    <strong>before</strong> the Incendium tag.
                  </Typography>
                  <Typography variant="body2" component="div" gutterBottom>
                    If no supported consent signal is detected, the tag applies
                    your configured <strong>no-consent behaviour</strong>{" "}
                    immediately — it does not wait for a CMP script to load.
                  </Typography>
                  <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>
                    Supported for auto-detect
                  </Typography>
                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      pl: 2.5,
                      "& li": { typography: "body2", mb: 0.5 },
                    }}
                  >
                    <li>
                      <strong>IAB TCF v2</strong> — purpose 8 enables pageviews;
                      purpose 7 additionally enables Meta CAPI (via{" "}
                      <Box component="span" sx={{ fontFamily: "monospace" }}>
                        __tcfapi
                      </Box>
                      )
                    </li>
                    <li>
                      <strong>Google Consent Mode v2</strong> —{" "}
                      <Box component="span" sx={{ fontFamily: "monospace" }}>
                        analytics_storage
                      </Box>{" "}
                      (purpose 8) enables pageviews and conversions;{" "}
                      <Box component="span" sx={{ fontFamily: "monospace" }}>
                        ad_storage
                      </Box>{" "}
                      (purpose 7) additionally enables Meta CAPI /{" "}
                      <Box component="span" sx={{ fontFamily: "monospace" }}>
                        _fbp
                      </Box>
                      /{" "}
                      <Box component="span" sx={{ fontFamily: "monospace" }}>
                        _fbc
                      </Box>
                    </li>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    CMPs that implement these standards (for example OneTrust,
                    Cookiebot, or CookieYes) are usually compatible, as long as
                    they run before the Incendium tag. Use{" "}
                    <strong>Manual integration</strong> if your stack uses a
                    different model.
                  </Typography>
                </Alert>
              ) : null}
              {capture === "manual" ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2" component="div" gutterBottom>
                    Use the <strong>inc</strong> function (same as{" "}
                    <Box component="span" sx={{ fontFamily: "monospace" }}>
                      window.inc
                    </Box>{" "}
                    once the tag has run) with Google Consent Mode-style keys.{" "}
                    <Box component="span" sx={{ fontFamily: "monospace" }}>
                      analytics_storage
                    </Box>{" "}
                    enables pageviews and conversions; add{" "}
                    <Box component="span" sx={{ fontFamily: "monospace" }}>
                      ad_storage
                    </Box>{" "}
                    for Meta CAPI. Call it from your CMP whenever consent
                    changes — Incendium updates its tracking tier and pushes a{" "}
                    <Box component="span" sx={{ fontFamily: "monospace" }}>
                      dataLayer
                    </Box>{" "}
                    consent <strong>update</strong> using only those keys (plus{" "}
                    <Box
                      component="span"
                      sx={{ fontFamily: "monospace" }}
                    >{`security_storage: 'granted'`}</Box>
                    ). Other GCM fields are ignored.
                  </Typography>
                  <Typography variant="body2" component="div" gutterBottom>
                    Event name:{" "}
                    <Box component="span" sx={{ fontFamily: "monospace" }}>
                      consent_update
                    </Box>
                    . Each value must be{" "}
                    <Box component="span" sx={{ fontFamily: "monospace" }}>
                      {`'granted'`}
                    </Box>{" "}
                    or{" "}
                    <Box component="span" sx={{ fontFamily: "monospace" }}>
                      {`'denied'`}
                    </Box>
                    . Supported keys:{" "}
                    <Box component="span" sx={{ fontFamily: "monospace" }}>
                      analytics_storage
                    </Box>{" "}
                    (purpose 8 — pageviews and conversions),{" "}
                    <Box component="span" sx={{ fontFamily: "monospace" }}>
                      ad_storage
                    </Box>{" "}
                    (purpose 7 — Meta CAPI /{" "}
                    <Box component="span" sx={{ fontFamily: "monospace" }}>
                      _fbp
                    </Box>
                    ).{" "}
                    <Box component="span" sx={{ fontFamily: "monospace" }}>
                      functionality_storage
                    </Box>{" "}
                    is accepted but not used for tiering yet. Partial updates
                    merge with previous values for these keys only.
                  </Typography>
                  <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>
                    Example — analytics + Meta CAPI (full)
                  </Typography>
                  <Box
                    component="pre"
                    sx={(theme) => ({
                      m: 0,
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "action.hover",
                      fontFamily:
                        "ui-monospace, SFMono-Regular, Menlo, Monaco, monospace",
                      fontSize: theme.typography.pxToRem(12),
                      lineHeight: 1.5,
                      overflowX: "auto",
                      whiteSpace: "pre-wrap",
                    })}
                  >
                    {`inc('consent_update', {
  analytics_storage: 'granted',
  ad_storage: 'granted',
});`}
                  </Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Example — analytics only (no Meta CAPI)
                  </Typography>
                  <Box
                    component="pre"
                    sx={(theme) => ({
                      m: 0,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: "action.hover",
                      fontFamily:
                        "ui-monospace, SFMono-Regular, Menlo, Monaco, monospace",
                      fontSize: theme.typography.pxToRem(12),
                      lineHeight: 1.5,
                      overflowX: "auto",
                      whiteSpace: "pre-wrap",
                    })}
                  >
                    {`inc('consent_update', {
  analytics_storage: 'granted',
  ad_storage: 'denied',
});`}
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1.5 }}
                  >
                    Until the first <strong>consent_update</strong>, behaviour
                    follows <strong>No-consent behaviour</strong> below. With{" "}
                    <strong>Anonymised tracking</strong>, the tag starts in
                    cookieless mode immediately and upgrades when analytics is
                    granted. With <strong>No tracking</strong>, nothing fires
                    until update. After update,{" "}
                    <strong>analytics_storage</strong> runs pageviews and
                    conversions; with <strong>ad_storage</strong> also granted,
                    Meta CAPI IDs are included.
                  </Typography>
                </Alert>
              ) : null}
            </Box>

            <Box>
              <SectionLabel gutterBottom display="block">
                No-consent behaviour
              </SectionLabel>
              <Stack spacing={1.5}>
                <ConsentOptionRow
                  selected={noConsent === "no_tracking"}
                  onSelect={() => setNoConsent("no_tracking")}
                  title="No tracking"
                  description="If the user has not consented (or consent is unknown), do not fire the tag for measurement."
                  disabled={formDisabled}
                />

                <ConsentOptionRow
                  selected={noConsent === "anonymised"}
                  onSelect={() => setNoConsent("anonymised")}
                  title="Anonymised tracking"
                  description={
                    capture === "manual"
                      ? "Start tracking immediately in cookieless, minimised mode (no persistent IDs or PII). When the user grants analytics via consent_update, upgrade to identified tracking."
                      : "Start immediately in cookieless, minimised mode while consent is unknown or refused; upgrade to identified tracking when analytics consent is granted."
                  }
                  recommended
                  disabled={formDisabled}
                />

                <ConsentOptionRow
                  selected={noConsent === "full"}
                  onSelect={() => setNoConsent("full")}
                  title="Full tracking"
                  description="Run the tag with full capabilities even when consent has not been granted. Only use with a confirmed lawful basis."
                  disabled={formDisabled}
                />
              </Stack>
            </Box>
          </>
        ) : (
          <Alert severity="info">
            <Typography variant="body2">
              Consent capture and no-consent behaviour settings only apply when{" "}
              <strong>Require consent before tracking</strong> is enabled.
            </Typography>
          </Alert>
        )}
      </Stack>

      <ActionsRow>
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleCancel}
          disabled={!dirty || formDisabled}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => void handleSave()}
          disabled={!dirty || formDisabled}
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </ActionsRow>
    </Box>
  );
}

export default ConsentModeSettings;
